import { faClock } from "@fortawesome/pro-regular-svg-icons/faClock";
import { faPlay } from "@fortawesome/pro-solid-svg-icons/faPlay";
import { faStop } from "@fortawesome/pro-solid-svg-icons/faStop";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Grid } from "@mui/material";
import { CorePermissions } from "app/appPermissions";
import { MprcDatePicker } from "components/MprcDatePicker";
import { MprcSwitch } from "components/MprcSwitch";
import { MprcTimePicker } from "components/MprcTimePicker";
import { CustomVideoSlide } from "components/types/lightBox";
import { FeedAttachDto, ShortVideoSettingsDto } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import moment, { Moment } from "moment";
import { useSession } from "providers/SessionProvider";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { OnProgressProps } from "react-player/base";
import { BuildLighboxSlide } from "views/knowledge/components/card/behaviors/common/CardLightbox";
import { ShortVideoPlayer } from "views/knowledge/shorts/components/ShortVideoPlayer";

////////////////////////////////////////////////////////////////////////////////

const MaxSeconds = 120;

type EditShortVideoValidation = {
    startTime?: boolean;
    endTime?: boolean;
    showUntil?: boolean;
};

function getSeconds(time: Moment | null) {
    return time == null ? 0 : time.hours() * 3600 + time.minutes() * 60 + time.seconds();
}

interface IProps {
    attach: FeedAttachDto;
    record: ShortVideoSettingsDto | null;
    onChange: (ShortVideoSettingsDto: Partial<ShortVideoSettingsDto>) => void;
    onReadyToSave: (valid: boolean) => void;
}
export const EditShortVideo = (props: IProps) => {
    const { attach, record, onChange, onReadyToSave } = props;
    const { t } = useTranslation("knowledge");
    const session = useSession();

    const [editMode, setEditMode] = useState<boolean>(true);
    const [validation, setValidation] = useState<EditShortVideoValidation>({ startTime: record?.startTime != null, endTime: record?.endTime != null, showUntil: true });
    const [startTime, setStartTime] = useState<Moment | null>(record?.startTime == null ? null : moment().startOf("day").seconds(record.startTime));
    const [endTime, setEndTime] = useState<Moment | null>(record?.endTime == null ? null : moment().startOf("day").seconds(record.endTime));

    const playTime = useRef<number>(0);
    const slide = BuildLighboxSlide(attach, record != null && !editMode) as CustomVideoSlide;

    const today = moment().startOf("day").toDate();
    const minDate = useRef<Date>(record?.dueDate == null || record.dueDate > today ? today : record.dueDate);

    ///
    useEffect(() => {
        if (record != null) {
            setStartTime(moment().startOf("day").seconds(record.startTime));
            setEndTime(moment().startOf("day").seconds(record.endTime));
            setValidation({ startTime: record.startTime != null, endTime: record.endTime != null, showUntil: true });
            minDate.current = record?.dueDate == null || record.dueDate > today ? today : record.dueDate;
        }
        playTime.current = 0;
    }, [attach]);

    ///
    useEffect(() => {
        onReadyToSave(Object.values(validation).reduce((prevValue, currentValue) => prevValue === true && currentValue === true, true)!);
    }, [validation, record]);

    ///
    const handleProgress = useCallback((id: string, state: OnProgressProps) => {
        playTime.current = state.playedSeconds;
    }, [attach]);

    ///
    const handleStartTime = () => {
        const s = moment().startOf("day").seconds(playTime.current);
        editStartTime(s);
        checkEditStartTime(s, endTime);
    };

    ///
    const handleEndTime = () => {
        const e = moment().startOf("day").seconds(playTime.current);
        editEndTime(e);
        checkEditEndTime(startTime, e);
    };

    ///
    const handlePlay = () => {
        setEditMode(state => !state);
    };

    ///
    const editStartTime = (newTime: Moment | null) => {
        var s = getSeconds(newTime);
        setValidation(state => ({ ...state, startTime: newTime != null && s >= 0 && s <= slide.duration }));
        setStartTime(newTime);
        onChange({ startTime: s });
    };

    ///
    const editEndTime = (newTime: Moment | null) => {
        var s = getSeconds(newTime);
        setValidation(state => ({ ...state, endTime: newTime != null && s >= 0 && s <= slide.duration }));
        setEndTime(newTime);
        onChange({ endTime: s });
    };

    ///
    const checkEditStartTime = (startTime: Moment | null, endTime: Moment | null) => {
        if (validation.startTime && startTime != null && endTime != null) {
            var d = endTime.diff(startTime, 'seconds');
            if (d < 0 || d > MaxSeconds) {
                const inc = Math.min(slide.duration - getSeconds(startTime), MaxSeconds);
                const newEndTime = moment(startTime).add(inc, "seconds");
                setValidation(state => ({ ...state, startTime: true, endTime: true }));
                setEndTime(newEndTime); onChange({ endTime: getSeconds(newEndTime) });
            }
        }
    };

    ///
    const checkEditEndTime = (startTime: Moment | null, endTime: Moment | null) => {
        if (validation.endTime && startTime != null && endTime != null) {
            var d = endTime.diff(startTime, 'seconds');
            if (d < 0 || d > MaxSeconds) {
                const inc = Math.min(getSeconds(endTime), MaxSeconds);
                const newStartTime = moment(endTime).subtract(inc, "seconds");
                setValidation(state => ({ ...state, startTime: true, endTime: true }));
                setStartTime(newStartTime); onChange({ startTime: getSeconds(newStartTime) });
            }
        }
    };

    return (
        <>
            <Box height={400} mb={.5}>
                <ShortVideoPlayer
                    {...slide}
                    height="inherit"
                    editMode={editMode}
                    startTime={record?.startTime ?? undefined}
                    endTime={record?.endTime ?? undefined}
                    onProgress={handleProgress}
                    onEnded={() => setEditMode(true)}
                />
            </Box>

            {
                record != null &&
                <>
                    <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
                        <Grid size={{ xs: 7 }}>

                            <Grid container justifyContent="center" alignItems="center" spacing={1}>
                                <Grid size={{ xs: 5 }}>
                                    <Button variant="contained" color="warning" onClick={handleStartTime} disabled={!editMode} fullWidth>{t("ShortVideo.Edit.StartTimeButton")}</Button>
                                </Grid>
                                <Grid size={{ xs: 2 }} sx={{ textAlign: "center" }}>
                                    {Formaters.playTimer(slide.duration)}
                                </Grid>
                                <Grid size={{ xs: 5 }}>
                                    <Button variant="contained" color="warning" onClick={handleEndTime} disabled={!editMode} fullWidth>{t("ShortVideo.Edit.EndTimeButton")}</Button>
                                </Grid>
                            </Grid>

                            <Grid container justifyContent="center" alignItems="center" spacing={1}>
                                <Grid size={{ xs: 5 }}>
                                    <MprcTimePicker
                                        disableOpenPicker
                                        error={validation.startTime === false}
                                        disabled={!editMode}
                                        endAdornment={<FontAwesomeIcon icon={faClock} />}
                                        onBlur={() => checkEditStartTime(startTime, endTime)}
                                        views={['hours', 'minutes', 'seconds']}
                                        format="HH:mm:ss"
                                        value={startTime?.toDate() ?? null}
                                        onError={(reason, v) => setValidation(state => ({ ...state, startTime: reason == null }))}
                                        onChange={(date, v) => editStartTime(moment(date))}
                                    />
                                </Grid>

                                <Grid size={{ xs: 2 }} sx={{ textAlign: "center" }}>
                                    <span>{t("ShortVideo.Edit.UntilLabel")}</span>
                                </Grid>

                                <Grid size={{ xs: 5 }}>
                                    <MprcTimePicker
                                        disableOpenPicker
                                        error={validation.endTime === false}
                                        disabled={!editMode}
                                        endAdornment={<FontAwesomeIcon icon={faClock} />}
                                        views={['hours', 'minutes', 'seconds']}
                                        format="HH:mm:ss"
                                        value={endTime?.toDate() ?? null}
                                        onError={(reason, v) => setValidation(state => ({ ...state, endTime: reason == null }))}
                                        onChange={(date, v) => editEndTime(moment(date))}
                                        onBlur={() => checkEditEndTime(startTime, endTime)}
                                    />
                                </Grid>
                            </Grid>

                        </Grid>

                        <Grid alignSelf="flex-start">
                            <Button variant="contained" color="info" onClick={handlePlay}>

                                <FontAwesomeIcon icon={editMode ? faPlay : faStop} fixedWidth />
                                <span>{t(editMode ? "ShortVideo.Edit.PlayInterval" : "ShortVideo.Edit.StopPlayback")}</span>
                            </Button>
                        </Grid>
                    </Grid>

                    <Grid container justifyContent="space-between" alignItems="flex-end" >
                        <Grid>

                            <MprcDatePicker
                                label={t("ShortVideo.Edit.DueDate")}
                                value={record.dueDate}
                                fullWidth
                                timePartHour="endDate"
                                minDate={minDate.current}
                                error={validation.showUntil === false}
                                onError={(reason, v) => setValidation(state => ({ ...state, showUntil: reason == undefined }))}
                                onChange={
                                    (date) => {
                                        setValidation(state => ({ ...state, showUntil: date == null || date >= minDate.current }));
                                        onChange({ dueDate: date });
                                    }}
                            />
                        </Grid>

                        {
                            session.permissionExplorer.hasOne([CorePermissions.KnowledgeEngagementManager]) &&
                            <Grid>
                                <MprcSwitch
                                    label={t("ShortVideo.Edit.PriorityView")}
                                    checked={record.priorityView || false}
                                    onChange={(e) => onChange({ priorityView: e.target.checked })}
                                />
                            </Grid>
                        }
                    </Grid>
                </>
            }
        </>
    );
};
